(function () {
  'use strict';

  const GROUPS = ['custom-frame', 'compost', 'feed', 'landscape-bark', 'soaker-hose', 'rootgrow', 'spiral-guard', 'defender-guard', 'stakes', 'expandable-guard', 'bamboo-poles', 'watering-timer'];
  const text = value => String(value == null ? '' : value).toLowerCase().replace(/[‐‑–—]/g, '-');
  const list = value => Array.isArray(value) ? value : value == null ? [] : [value];
  const id = value => String(value == null ? '' : value);
  const displayLabel = (product, variant) => product.group === 'custom-frame' && Array.isArray(variant.options) && variant.options.length === 2
    ? variant.options[0] + ' W × ' + variant.options[1] + ' H'
    : (product.label || product.title) + (variant.title === 'Default Title' ? '' : ' — ' + variant.title);
  const EXCLUDED = /\b(rhododendron\w*|azalea\w*|heathers?|calluna\w*|erica|cranberr\w*|blueberr\w*|vaccinium\w*|brassica\w*)\b/;
  const ERICOID = /\b(rhododendron\w*|azalea\w*|heathers?|calluna\w*|erica|cranberr\w*|blueberr\w*|vaccinium\w*)\b/;
  const ACID = /\b(ericaceous|acid[- ]loving|rhododendron\w*|azalea\w*|heathers?|calluna\w*|erica|cranberr\w*|blueberr\w*|vaccinium\w*|camellia\w*|pieris)\b/;

  function categoriesFor(plant) {
    const explicit = list(plant.categories).map(text);
    const type = text(plant.type || plant.product_type).replace(/\s+/g, '-');
    const collections = list(plant.collections).map(collection => text(typeof collection === 'object' ? collection.handle : collection));
    if (collections.includes('accessories') || /accessor|compost|fertili|rootgrow|root-treatment|guard|stake|support|hose|irrigation|watering-equipment|tool|plant-food|planting-kit|bark|topsoil|top-soil|manure|gift-card|artificial|herbicide|weed-killer|weed-control|pesticid|fungicid|insecticid|shear|secateur|spade|planter/.test(type)) return [];
    const source = [type, ...list(plant.tags), ...collections, ...explicit].map(text).join(' ');
    const result = new Set(explicit.filter(category => ['hedging', 'full-standard', 'pleached', 'topiary', 'underplanting', 'grasses', 'ferns', 'climbers', 'bamboo', 'trees', 'ornamental'].includes(category)));
    if (/hedg/.test(source)) result.add('hedging');
    if (/\bwhips\b/.test(type)) result.add('hedging');
    if (/full[- ]standards?/.test(source)) result.add('full-standard');
    if (/pleach/.test(source)) result.add('pleached');
    if (/topiary/.test(source)) result.add('topiary');
    if (/underplant|border[- ]collection|ready[- ]made[- ]borders?/.test(source)) result.add('underplanting');
    if (/\bgrass(?:es)?\b/.test(source)) result.add('grasses');
    if (/\bferns?\b/.test(source)) result.add('ferns');
    if (/\bclimb(?:ers?|ing)\b/.test(source)) result.add('climbers');
    if (/\bbamboos?\b/.test(source)) result.add('bamboo');
    if (!result.size && /\btrees?\b|\bconifers?\b/.test(source)) result.add('trees');
    if (!result.size && /perennial|herbaceous|shrub|annual|biennial|bedding|bulb|\broses?\b|\balpines?\b|\bherbs?\b|succulent|ground[- ]?cover|ornamental[- ]plants?/.test(source)) result.add('ornamental');
    if (!result.size && (['plant', 'plants', 'outdoor-plants', 'indoor-plants', 'garden-plants', 'potted-plants'].includes(type) || collections.some(handle => ['plants', 'all-plants', 'garden-plants'].includes(handle)))) result.add('ornamental');
    return [...result];
  }

  function optionValue(plant, variant, expression) {
    const names = list(plant.optionNames || plant.options);
    const values = list(variant.options);
    for (let index = 0; index < names.length; index += 1) {
      const name = typeof names[index] === 'object' ? names[index].name : names[index];
      if (expression.test(text(name))) return values[index] || variant['option' + (index + 1)] || '';
    }
    return '';
  }

  function rootType(plant, variant) {
    const raw = text(variant.rootType || plant.rootType || optionValue(plant, variant, /root/));
    const fallback = raw || text([variant.title, ...list(variant.options), plant.type, plant.product_type].join(' '));
    if (/bare[- ]?root/.test(fallback)) return 'bare-root';
    if (/root[- ]?ball/.test(fallback)) return 'rootball';
    if (/pot[- ]?grown|container|\bpot\b/.test(fallback)) return 'container';
    return 'unknown';
  }

  function heightRange(plant, variant) {
    const explicit = variant.heightCm || plant.heightCm;
    if (explicit != null) {
      const min = Number(typeof explicit === 'object' ? explicit.min : explicit);
      const max = Number(typeof explicit === 'object' ? explicit.max : explicit);
      if (Number.isFinite(min) && Number.isFinite(max) && min > 0 && max >= min) return { min, max };
    }
    const named = optionValue(plant, variant, /height/);
    const raw = text(named || variant.height || variant.title);
    // Only a value with explicit height units is eligible; girth and pot volume are not heights.
    if (/girth|trunk|stem|litre|liter|\bltr\b/.test(raw)) return null;
    if (/\b(over|above|under|below|less than|more than|at least)\b|[<>+]/.test(raw)) return null;
    const range = raw.match(/(\d+(?:\.\d+)?)\s*(cm|m)?\s*(?:-|to)\s*(\d+(?:\.\d+)?)\s*(cm|m)\b/);
    if (range) {
      const unit = range[4];
      const min = Number(range[1]) * ((range[2] || unit) === 'm' ? 100 : 1);
      const max = Number(range[3]) * (unit === 'm' ? 100 : 1);
      return min > 0 && max >= min ? { min, max } : null;
    }
    const single = raw.match(/(?:^|\s)(\d+(?:\.\d+)?)\s*(cm|m)\b/);
    if (!single) return null;
    const value = Number(single[1]) * (single[2] === 'm' ? 100 : 1);
    return value > 0 ? { min: value, max: value } : null;
  }

  function guardBand(range) {
    if (!range || !Number.isFinite(range.min) || !Number.isFinite(range.max)) return 'unknown';
    if (range.max <= 90) return 'spiral-guard';
    if (range.min > 90) return 'defender-guard';
    return 'uncertain';
  }

  function buildNeeds(input) {
    const groups = new Set();
    const compost = new Set();
    const rootgrowModes = new Set();
    const rootgrowNeeds = new Set();
    const warnings = new Set();
    function visit(plant, inheritedQuantity) {
      const quantity = Number(plant.quantity == null ? inheritedQuantity : plant.quantity);
      if (!Number.isFinite(quantity) || quantity <= 0) return;
      const variant = plant.variant || plant;
      if (variant.available === false) return;
      const categories = categoriesFor(plant);
      if (!categories.length) return;
      const identity = text([plant.title, plant.handle, ...list(plant.tags)].join(' '));
      const mixed = plant.mixed === true || /mixed/.test(text(plant.type || plant.product_type));
      if (Array.isArray(plant.components) && plant.components.length) {
        plant.components.forEach(component => visit({ ...plant, ...component, mixed: false, components: null, variant: component.variant || variant, quantity: quantity * Number(component.count || component.quantity || 1) }, 1));
        return;
      }
      const acid = plant.acidLoving === true || (plant.acidLoving !== false && ACID.test(identity));
      if (!mixed || typeof plant.acidLoving === 'boolean') {
        groups.add('compost');
        compost.add(acid ? 'ericaceous' : 'general');
      } else warnings.add('Mixed-pack composition is needed to select suitable compost and root treatments.');
      groups.add('feed');
      groups.add('landscape-bark');
      groups.add('soaker-hose');
      // Mixed packs without a known composition get a watering option, not a guessed compost mix.
      if (mixed && typeof plant.acidLoving !== 'boolean') groups.add('watering-timer');
      if (categories.includes('full-standard') || categories.includes('pleached')) groups.add('stakes');
      if (categories.includes('full-standard')) groups.add('expandable-guard');
      if (categories.includes('pleached')) { groups.add('custom-frame'); groups.add('bamboo-poles'); }
      const root = rootType(plant, variant);
      if (mixed) return;
      if (root === 'bare-root' || root === 'rootball') {
        const excluded = plant.rootgrowExcluded === true || EXCLUDED.test(identity);
        if (excluded) {
          if (ERICOID.test(identity)) { rootgrowModes.add('ericoid'); rootgrowNeeds.add('ericoid'); }
        } else if (root === 'bare-root') {
          rootgrowNeeds.add('bare-root');
          rootgrowModes.add('gel');
          rootgrowModes.add('professional');
        } else {
          rootgrowNeeds.add('rootball');
          rootgrowModes.add('ordinary');
          rootgrowModes.add('professional');
        }
      }
      const whip = plant.whip === true || (plant.whip !== false && categories.includes('hedging') && !categories.includes('full-standard') && !categories.includes('pleached'));
      if (root === 'bare-root' && whip) {
        const band = guardBand(heightRange(plant, variant));
        if (GROUPS.includes(band)) groups.add(band);
        else warnings.add('Confirm the supplied whip height before choosing a guard; ranges crossing 90cm need a specific choice.');
      }
    }
    list(input).forEach(plant => visit(plant, 1));
    if (rootgrowModes.size) groups.add('rootgrow');
    return { groups: GROUPS.filter(group => groups.has(group)), compost: [...compost], rootgrowModes: [...rootgrowModes], rootgrowNeeds: [...rootgrowNeeds], warnings: [...warnings] };
  }

  function eligibleVariants(catalog, needs, basketItems) {
    const results = [];
    list(catalog).forEach(product => {
      list(product.variants).forEach(variant => {
        const ownership = variantState(basketItems, variant.id);
        let suitable = needs.groups.includes(product.group);
        if (product.group === 'compost') suitable = suitable && needs.compost.includes(product.ericaceous === true ? 'ericaceous' : 'general');
        const mode = variant.rootgrowMode || product.rootgrowMode;
        if (product.group === 'rootgrow') suitable = suitable && needs.rootgrowModes.includes(mode);
        if (product.group === 'feed' && text(variant.title).trim() !== 'controlled release') return;
        // Existing extras remain editable. Suitability still controls suggestions for new items.
        if (ownership.quantity === 0 && (!suitable || variant.available !== true)) return;
        results.push({ product, variant, ownership, suitable });
      });
    });
    return results;
  }

  function variantState(items, variantId) {
    const lines = list(items).filter(item => id(item.variant_id || item.id || item.variant?.id) === id(variantId) && Number(item.quantity) > 0);
    return { lines, quantity: lines.reduce((sum, item) => sum + Number(item.quantity), 0), line: lines.length === 1 ? lines[0] : null, ambiguous: lines.length > 1 };
  }

  function quantityError(quantity, state, variant) {
    if (!Number.isSafeInteger(quantity) || quantity < 0) return 'Enter a whole number of zero or more.';
    if (quantity === 0) return state.quantity > 0 ? '' : 'Choose a quantity of at least one.';
    if (variant.available !== true && quantity > state.quantity) return 'This option is unavailable for an increase. You can reduce or remove the quantity already in your basket.';
    const rule = variant.quantityRule || {};
    const min = Number(rule.min) > 0 ? Number(rule.min) : 1;
    const step = Number(rule.increment) > 0 ? Number(rule.increment) : 1;
    const max = Number(rule.max) > 0 ? Number(rule.max) : Infinity;
    if (quantity < min || (quantity - min) % step !== 0 || quantity > max) return 'Choose a valid quantity: minimum ' + min + ', increments of ' + step + (Number.isFinite(max) ? ', maximum ' + max : '') + '.';
    return '';
  }

  function planChange(items, variant, desired, expected) {
    const state = variantState(items, variant.id);
    const error = quantityError(desired, state, variant);
    if (error) return { kind: 'error', message: error };
    if (desired === state.quantity) return { kind: 'none', state };
    if (state.quantity !== expected) return { kind: 'conflict', message: 'The basket quantity changed. Review the current quantity and update again.', state };
    if (state.ambiguous) return { kind: 'error', message: 'This option has separate basket lines. Adjust those lines in your basket to keep their options unchanged.', state };
    if (state.line) {
      if (!state.line.key) return { kind: 'error', message: 'Use your basket to update this option safely.', state };
      // Send only quantity and the exact key: existing properties and selling plans remain intact.
      return { kind: 'change', payload: { id: state.line.key, quantity: desired }, state };
    }
    return { kind: 'add', payload: { items: [{ id: variant.id, quantity: desired }] }, state };
  }

  function automaticVariant(group, options) {
    // A single support/formulation still needs a fit decision; only unambiguous consumables default.
    return ['compost', 'feed', 'landscape-bark'].includes(group) && options.length === 1 ? id(options[0].variant.id) : '';
  }

  function resolveVariantEvent(product, detail) {
    if (!product || !detail || id(detail.product?.id) !== id(product.id)) return null;
    if (detail.variant) {
      const known = list(product.variants).find(variant => id(variant.id) === id(detail.variant.id));
      return known ? { resolved: true, variant: { ...known, ...detail.variant } } : null;
    }
    if (detail.variant === false || detail.state === 'not-selected' || detail.state === 'unavailable') return { resolved: false, variant: null };
    return null;
  }


  function customFrameSelection(catalog, selection, plants) {
    if (!selection || selection.active !== true) return null;
    const belongs = list(plants).some(plant => categoriesFor(plant).includes('pleached') && id(plant.productId || plant.product_id || plant.id) === id(selection.productId));
    if (!belongs) return null;
    let found = null;
    list(catalog).filter(product => product.group === 'custom-frame').some(product => {
      const variant = list(product.variants).find(variant => id(variant.id) === id(selection.variantId) && variant.available === true && list(variant.options)[0] === selection.width && list(variant.options)[1] === selection.height);
      if (!variant) return false;
      const rule = variant.quantityRule || {}, minimum = Math.max(3, Number(rule.min) || 1), increment = Math.max(1, Number(rule.increment) || 1);
      const requested = Number.isSafeInteger(selection.quantity) && selection.quantity > 0 ? selection.quantity : minimum;
      const quantity = minimum + Math.ceil(Math.max(0, requested - minimum) / increment) * increment;
      if (Number(rule.max) > 0 && quantity > Number(rule.max)) return false;
      found = { product, variant, quantity }; return true;
    });
    return found;
  }

  const rules = Object.freeze({ customFrameSelection, categoriesFor, rootType, heightRange, guardBand, buildNeeds, eligibleVariants, automaticVariant, variantState, quantityError, planChange, resolveVariantEvent });
  if (typeof module !== 'undefined' && module.exports) module.exports = rules;
  if (typeof document === 'undefined') return;

  if (window.HarrodPlantingExtrasRuntime) {
    window.HarrodPlantingExtrasRuntime.init();
    return;
  }

  const draftKey = () => 'ho-planting-extras-draft:' + location.pathname;
  const cartForm = () => [...document.forms].find(form => !form.matches('[data-ho-extra-form]') && /\/cart\/?$/.test(new URL(form.action, location.href).pathname));
  const rootKey = root => root.id || root.getAttribute('aria-labelledby') || root.dataset.context;
  function fieldIdentity(field) {
    if (field.name === 'note') return 'note';
    if (/^updates\[[^\]]+\]$/.test(field.name)) return field.name;
    const row = field.closest('[data-cartitem-key],[data-cart-item-key],[data-item-key]');
    const key = row && (row.dataset.cartitemKey || row.dataset.cartItemKey || row.dataset.itemKey);
    return key ? 'line:' + key : '';
  }
  function captureDraft() {
    const form = cartForm();
    const changed = form ? [...form.querySelectorAll('[name="note"],input[name^="updates"]')].filter(field => field.value !== field.defaultValue).map(field => ({ key: fieldIdentity(field), value: field.value })) : [];
    const extras = [];
    runtime.instances.forEach(instance => instance.cards.forEach(card => {
      const state = instance.cardState(card);
      const select = card.querySelector('[data-ho-extra-variant]');
      if (select.value) extras.push({ root: rootKey(instance.root), group: card.dataset.hoExtraCard, variant: select.value, quantity: card.querySelector('[data-ho-extra-quantity]').value, dirty: state.dirty, base: state.baseQuantity });
    }));
    return { fields: changed.filter(field => field.key), extras, dirty: changed.length > 0, unkeyed: changed.some(field => !field.key) };
  }
  function saveDraft(draft) {
    if (draft.unkeyed) return false;
    try { sessionStorage.setItem(draftKey(), JSON.stringify({ ...draft, at: Date.now() })); return true; }
    catch (_) { return !draft.dirty && !draft.extras.some(item => item.dirty); }
  }
  function restoreDraft() {
    try {
      const value = sessionStorage.getItem(draftKey());
      if (!value) return;
      sessionStorage.removeItem(draftKey());
      const draft = JSON.parse(value);
      if (!Array.isArray(draft.fields) || Date.now() - draft.at > 300000) return;
      const form = cartForm();
      if (form) [...form.querySelectorAll('[name="note"],input[name^="updates"]')].forEach(field => {
        const saved = draft.fields.find(item => item.key === fieldIdentity(field));
        if (saved && saved.key) field.value = saved.value;
      });
      runtime.draftExtras = list(draft.extras);
    } catch (_) { /* Leave ordinary cart fields intact if browser storage is unavailable. */ }
  }

  const runtime = {
    instances: new Set(), cart: null, pending: 0, queue: Promise.resolve(), reloadRequested: false, draftExtras: [], reloadTimer: null,
    publish(cart) {
      if (!cart || !Array.isArray(cart.items)) return;
      this.cart = cart;
      this.instances.forEach(instance => instance.receiveCart(cart));
      if (Number.isSafeInteger(cart.item_count)) document.querySelectorAll('[data-header-cart-count]').forEach(badge => {
        badge.setAttribute('data-header-cart-count', String(cart.item_count));
        badge.classList.toggle('visible', cart.item_count > 0);
      });
      document.dispatchEvent(new CustomEvent('ho:planting-extras-cart-updated', { detail: { cart } }));
    },
    enqueue(job) {
      clearTimeout(this.reloadTimer);
      if (this.pending === 0) {
        this.checkoutButtons = [...(cartForm()?.elements || [])].filter(control => control.name === 'checkout' && 'disabled' in control).map(button => ({ button, disabled: button.disabled }));
        this.checkoutButtons.forEach(item => { item.button.disabled = true; });
      }
      this.pending += 1;
      const result = this.queue.then(job);
      this.queue = result.catch(() => {});
      return result.finally(() => {
        this.pending -= 1;
        if (this.pending === 0) {
          (this.checkoutButtons || []).forEach(item => { item.button.disabled = item.disabled; });
          if (this.reloadRequested) this.reloadTimer = setTimeout(() => {
            if (this.pending) return;
            this.reloadRequested = false;
            if (saveDraft(captureDraft())) location.reload();
            else this.instances.forEach(instance => instance.showStatus('Your basket was updated. Use View basket for current totals; keep a copy of unsaved changes first.', null, true));
          }, 80);
        }
      });
    },
    init() {
      this.instances.forEach(instance => {
        if (!instance.root.isConnected) instance.dispose();
      });
      document.querySelectorAll('[data-ho-planting-extras]:not([data-ho-extras-ready])').forEach(root => {
        try { new PlantingExtras(root); } catch (_) { /* The existing browse link remains the fallback. */ }
      });
    }
  };

  class PlantingExtras {
    constructor(root) {
      this.root = root;
      this.listeners = [];
      this.disposed = false;
      this.config = JSON.parse(root.querySelector('[data-ho-extra-config]').textContent);
      this.catalog = list(this.config.catalog);
      this.cards = [...root.querySelectorAll('[data-ho-extra-card]')].sort((left, right) => Number(right.dataset.hoExtraCard === 'custom-frame') - Number(left.dataset.hoExtraCard === 'custom-frame'));
      this.states = new WeakMap();
      this.basket = list(this.config.cart && this.config.cart.items);
      if (!runtime.cart) runtime.cart = this.config.cart || { items: [] };
      else this.receiveCart(runtime.cart, false);
      this.current = this.config.currentProduct;
      this.selectedId = id(this.current && this.current.selectedVariantId);
      this.selectionResolved = true;
      this.eventVariant = null;
      if (this.current) {
        const selected = [...document.querySelectorAll('[data-ho-vp-row][data-selected="true"] [name="id"],form:not([data-ho-extra-form]) select[name="id"]')].find(input => this.current.variants.some(variant => id(variant.id) === id(input.value)));
        if (selected) this.selectedId = id(selected.value);
      }
      this.listen(document, 'ho:custom-frame-selection', event => { this.frameSelection = event.detail; this.syncCustomFrame(); });
      try { this.frameSelection = JSON.parse(sessionStorage.getItem('ho-custom-frame-selection') || 'null'); } catch (_) { this.frameSelection = null; }
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: this.config.currency || 'GBP' });
      this.listen(root, 'submit', event => this.submit(event), true);
      this.listen(root, 'change', event => {
        const card = event.target.closest('[data-ho-extra-card]');
        if (!card) return;
        if (event.target.matches('[data-ho-extra-variant]')) {
          this.cardState(card).dirty = false;
          this.cardState(card).frameAuto = false;
          this.showStatus('', card.querySelector('form'), false);
        }
        this.updateCard(card);
      });
      this.listen(root, 'input', event => {
        const card = event.target.closest('[data-ho-extra-card]');
        if (card && event.target.matches('[data-ho-extra-quantity]')) {
          this.cardState(card).dirty = true;
          this.cardState(card).frameAuto = false;
          this.updateCard(card);
        }
      });
      if (this.config.context === 'product' && this.current) {
        this.watchProduct();
        if (categoriesFor(this.current).some(category => ['trees', 'full-standard', 'pleached'].includes(category))) this.watchTreeAdditions();
      }
      runtime.instances.add(this);
      this.refresh();
      runtime.draftExtras.filter(saved => saved.root === rootKey(root)).forEach(saved => {
        const card = this.cards.find(item => item.dataset.hoExtraCard === saved.group);
        if (!card || !this.allowed.some(item => id(item.variant.id) === saved.variant)) return;
        card.querySelector('select').value = saved.variant;
        this.updateCard(card, true);
        if (saved.dirty) {
          card.querySelector('[data-ho-extra-quantity]').value = saved.quantity;
          Object.assign(this.cardState(card), { dirty: true, baseQuantity: Number(saved.base) });
          this.updateCard(card);
        }
      });
      root.dataset.hoExtrasReady = 'true';
    }
    listen(target, type, listener, options) {
      target.addEventListener(type, listener, options);
      this.listeners.push({ target, type, listener, options });
    }
    dispose() {
      if (this.disposed) return;
      this.disposed = true;
      clearTimeout(this.timer);
      this.clearTreeReveal();
      this.observer?.disconnect();
      this.listeners.forEach(({ target, type, listener, options }) => target.removeEventListener(type, listener, options));
      this.listeners = [];
      runtime.instances.delete(this);
      delete this.root.dataset.hoExtrasReady;
      // Queued basket writes still finish; detached UI no longer receives events.
    }
    cardState(card) {
      if (!this.states.has(card)) this.states.set(card, { variant: '', baseQuantity: 0, dirty: false, pending: false, phase: '' });
      return this.states.get(card);
    }
    receiveCart(cart, render = true) {
      this.basket = list(cart.items).map(item => {
        const old = this.basket.find(previous => previous.key && previous.key === item.key) || this.basket.find(previous => id(previous.id || previous.variant_id) === id(item.id || item.variant_id)) || {};
        return { ...old, ...item, type: item.product_type || item.type || old.type, title: item.product_title || old.title || item.title };
      });
      if (render) this.refresh();
    }
    watchTreeAdditions() {
      this.listen(document, 'ho:product-added', event => {
        if (id(event.detail?.productId) === id(this.current.id)) this.revealAfterTreeAdded(false);
      });
      this.listen(document, 'addToCartFlyout:complete', event => {
        const data = event.detail?.responseData;
        const items = data && Array.isArray(data.items) ? data.items : data ? [data] : [];
        if (event.detail?.success === true && items.some(item => id(item.product_id) === id(this.current.id))) this.revealAfterTreeAdded(true);
      });
    }
    clearTreeReveal() {
      this.treeRevealObserver?.disconnect();
      this.treeRevealObserver = null;
      window.cancelAnimationFrame(this.treeRevealFrame);
      window.clearTimeout(this.treeRevealTimer);
      this.treeRevealTimer = null;
    }
    revealAfterTreeAdded(native) {
      this.clearTreeReveal();
      if (this.disposed || !this.root.isConnected || !this.root.querySelector('[data-ho-extra-primary]')?.children.length) return;
      const header = document.querySelector('[data-site-header]');
      const reveal = () => {
        this.clearTreeReveal();
        if (this.disposed || !this.root.isConnected) return;
        this.root.style.scrollMarginTop = Math.ceil((header?.getBoundingClientRect().height || 0) + 16) + 'px';
        this.root.tabIndex = -1;
        this.root.focus({ preventScroll: true });
        this.root.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      };
      if (!native || !header) { this.treeRevealTimer = window.setTimeout(reveal, 2000); return; }
      // Let Empire finish opening and release its focus trap before moving to the extras.
      const existing = new Set(header.querySelectorAll('[data-atc-banner]'));
      let banner = null, closing = false;
      this.treeRevealObserver = new MutationObserver(() => {
        if (this.disposed || !this.root.isConnected) { this.clearTreeReveal(); return; }
        if (!banner) {
          const banners = Array.from(header.querySelectorAll('[data-atc-banner]'));
          banner = banners.find(item => !existing.has(item)) || banners.find(item => item.getAttribute('data-animation-state') === 'open');
        }
        if (!banner) return;
        if (!banner.isConnected) { reveal(); return; }
        if (!closing && banner.getAttribute('data-animation-state') === 'open' && !banner.hasAttribute('data-animation')) {
          const close = banner.querySelector('[data-atc-banner-close]');
          if (!close) return;
          closing = true;
          this.treeRevealTimer = window.setTimeout(() => {
            this.treeRevealTimer = null;
            if (!this.disposed && close.isConnected) close.click();
          }, 2000);
        }
      });
      this.treeRevealObserver.observe(header, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-animation', 'data-animation-state'] });
    }
    watchProduct() {
      const handler = event => {
        if (this.root.contains(event.target)) return;
        const row = event.target.closest('[data-ho-vp-row]');
        const chosen = event.target.matches('[name="id"]') ? event.target.value : row && row.querySelector('[name="id"]')?.value;
        if (chosen && this.current.variants.some(variant => id(variant.id) === id(chosen))) {
          this.selectedId = id(chosen);
          this.selectionResolved = true;
          this.eventVariant = null;
        }
        const form = event.target.closest('form[data-product-form]');
        if (['input', 'change'].includes(event.type) && event.target.matches('[name="quantity"]') && form && this.frameSelection?.active === true && id(this.frameSelection.productId) === id(this.current.id)) {
          const mainId = form.querySelector('[data-variants][name="id"], [name="id"]')?.value;
          if (this.current.variants.some(variant => id(variant.id) === id(mainId))) {
            const quantity = Number(event.target.value);
            this.frameSelection = { ...this.frameSelection, quantity: Number.isSafeInteger(quantity) && quantity > 0 ? quantity : null };
            try { sessionStorage.setItem('ho-custom-frame-selection', JSON.stringify(this.frameSelection)); } catch (_) { /* Optional persistence. */ }
            this.syncCustomFrame();
          }
        }
        if (chosen || event.target.closest('[data-ho-quick-row],[data-ho-vp-root],[data-ho-quick-qty]')) this.schedule();
      };
      ['input', 'change', 'click'].forEach(name => this.listen(document, name, handler));
      // Pixel Union dispatches this non-bubbling event before it updates the hidden variant ID.
      this.listen(document, 'variant-change', event => {
        const selection = resolveVariantEvent(this.current, event.detail);
        if (!selection) return;
        this.selectionResolved = selection.resolved;
        this.eventVariant = selection.variant;
        this.selectedId = selection.variant ? id(selection.variant.id) : '';
        this.schedule();
      }, true);
      this.observer = new MutationObserver(records => {
        if (records.some(record => record.target.matches('[data-ho-vp-row]'))) {
          const active = document.querySelector('[data-ho-vp-row][data-selected="true"] [name="id"]');
          if (active) {
            this.selectedId = id(active.value);
            this.selectionResolved = true;
            this.eventVariant = null;
          }
          this.schedule();
        }
      });
      document.querySelectorAll('[data-ho-vp-row]').forEach(row => this.observer.observe(row, { attributes: true, attributeFilter: ['data-selected'] }));
    }
    schedule() {
      if (this.disposed) return;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.refresh(), 0);
    }
    lines() {
      if (this.config.context === 'cart') {
        const extras = new Set(this.catalog.map(product => id(product.id)));
        return this.basket.filter(item => !extras.has(id(item.productId || item.product_id)));
      }
      const variants = list(this.current && this.current.variants);
      const selected = [...document.querySelectorAll('[data-ho-quick-row]')].map(row => ({ row, quantity: Number(row.querySelector('input')?.value) })).filter(item => item.quantity > 0 && item.row.dataset.available !== 'false').map(item => ({ ...this.current, variant: variants.find(variant => id(variant.id) === item.row.dataset.variantId), quantity: item.quantity })).filter(item => item.variant);
      if (selected.length) return selected;
      if (!this.selectionResolved) return [];
      const variant = this.eventVariant || variants.find(item => id(item.id) === this.selectedId) || variants.find(item => item.available);
      return variant ? [{ ...this.current, variant, quantity: 1 }] : [];
    }
    refresh() {
      if (this.disposed) return;
      this.needs = buildNeeds(this.lines());
      this.allowed = eligibleVariants(this.catalog, this.needs, this.basket);
      this.cards.forEach(card => {
        const state = this.cardState(card);
        if (state.pending && state.selectedRecord && !this.allowed.some(item => id(item.variant.id) === id(state.selectedRecord.variant.id))) this.allowed.push(state.selectedRecord);
      });
      const primary = this.root.querySelector('[data-ho-extra-primary]');
      const more = this.root.querySelector('[data-ho-extra-more-items]');
      const pool = this.root.querySelector('[data-ho-extra-pool]');
      let visible = 0;
      this.cards.forEach(card => {
        const group = card.dataset.hoExtraCard;
        const select = card.querySelector('select');
        const eligible = this.allowed.filter(item => item.product.group === group);
        eligible.forEach(item => {
          if ([...select.options].some(option => option.value === id(item.variant.id))) return;
          const option = document.createElement('option');
          option.value = id(item.variant.id);
          option.textContent = displayLabel(item.product, item.variant) + ' — ' + this.money.format(Number(item.variant.price) / 100);
          select.append(option);
        });
        [...select.options].forEach(option => {
          if (!option.value) return;
          const allowed = eligible.some(item => id(item.variant.id) === option.value);
          option.hidden = !allowed;
          option.disabled = !allowed;
        });
        const hasOptions = eligible.length > 0;
        card.hidden = !hasOptions;
        if (hasOptions) visible += 1;
        const destination = hasOptions ? (this.config.context === 'product' || visible <= 4 ? primary : more) : pool;
        if (card.parentNode !== destination) destination.append(card);
        this.updateCard(card);
      });
      const disclosure = this.root.querySelector('[data-ho-extra-more]');
      if (disclosure) disclosure.hidden = visible <= 4;
      this.root.querySelector('[data-ho-extra-fallback]').hidden = visible > 0;
      if (!this.hasMessage) this.root.querySelector('[data-ho-extras-status]').textContent = this.needs.warnings.join(' ');
      this.syncCustomFrame();
    }

    syncCustomFrame() {
      const detail = this.frameSelection;
      if (!detail) return;
      const plants = this.lines(), relevant = plants.some(plant => categoriesFor(plant).includes('pleached') && id(plant.productId || plant.product_id || plant.id) === id(detail.productId));
      if (!relevant) return;
      const card = this.cards.find(item => item.dataset.hoExtraCard === 'custom-frame');
      if (!card) return;
      const state = this.cardState(card);
      if (state.pending) return;
      const token = JSON.stringify([detail.productId, detail.variantId, detail.width, detail.height, detail.quantity, detail.active]);
      if (state.frameToken === token) return;
      if (detail.active !== true) {
        state.frameToken = token;
        if (state.frameAuto && !variantState(this.basket, state.variant).quantity) {
          card.querySelector('[data-ho-extra-variant]').value = ''; state.frameAuto = false; state.dirty = false;
          this.updateCard(card);
        }
        return;
      }
      const found = customFrameSelection(this.catalog, detail, plants);
      if (!found || !this.allowed.some(item => id(item.variant.id) === id(found.variant.id))) return;
      const select = card.querySelector('[data-ho-extra-variant]');
      select.value = id(found.variant.id); this.updateCard(card);
      const ownership = variantState(this.basket, found.variant.id), quantity = card.querySelector('[data-ho-extra-quantity]');
      // A calculator selection is an editable suggestion; basket writes still require Add/Update.
      quantity.value = String(ownership.quantity || found.quantity);
      state.baseQuantity = ownership.quantity; state.dirty = !ownership.quantity; state.frameAuto = true; state.frameToken = token;
      this.updateCard(card);
    }

    updateCard(card, force = false) {
      const state = this.cardState(card);
      const form = card.querySelector('[data-ho-extra-form]');
      const select = card.querySelector('[data-ho-extra-variant]');
      const options = this.allowed.filter(item => item.product.group === card.dataset.hoExtraCard);
      const owned = options.filter(item => item.ownership.quantity > 0);
      const defaultId = automaticVariant(card.dataset.hoExtraCard, options);
      if (!select.value) select.value = owned.length === 1 ? id(owned[0].variant.id) : defaultId;
      let found = options.find(item => id(item.variant.id) === select.value);
      if (!found && !state.pending) { select.value = owned.length === 1 ? id(owned[0].variant.id) : defaultId; found = options.find(item => id(item.variant.id) === select.value); }
      form.classList.toggle('is-simple', Boolean(defaultId));
      const quantity = card.querySelector('[data-ho-extra-quantity]');
      const button = card.querySelector('[data-ho-extra-add]');
      const ownership = found ? variantState(this.basket, found.variant.id) : { quantity: 0, ambiguous: false };
      const rule = found && found.variant.quantityRule || {};
      const changed = state.variant !== select.value;
      if (changed || force || !state.dirty && state.baseQuantity !== ownership.quantity) {
        quantity.value = String(ownership.quantity || (Number(rule.min) > 0 ? rule.min : 1));
        state.baseQuantity = ownership.quantity;
        state.dirty = false;
      }
      state.variant = select.value;
      quantity.min = ownership.quantity > 0 ? '0' : String(Number(rule.min) > 0 ? rule.min : 1);
      quantity.step = '1';
      if (found && found.variant.available !== true && ownership.quantity > 0) quantity.max = String(ownership.quantity);
      else if (Number(rule.max) > 0) quantity.max = String(rule.max);
      else quantity.removeAttribute('max');
      quantity.setCustomValidity(found ? quantityError(Number(quantity.value), ownership, found.variant) : '');
      let basketLabel = card.querySelector('[data-ho-extra-in-basket]');
      if (!basketLabel) {
        basketLabel = document.createElement('span');
        basketLabel.className = 'hpe-card__in-basket';
        basketLabel.dataset.hoExtraInBasket = '';
        basketLabel.id = quantity.id + '-in-basket';
        quantity.parentElement.querySelector('label')?.insertAdjacentElement('afterend', basketLabel);
        quantity.setAttribute('aria-describedby', [quantity.getAttribute('aria-describedby'), basketLabel.id].filter(Boolean).join(' '));
      }
      basketLabel.hidden = !found;
      basketLabel.textContent = 'In basket: ' + ownership.quantity;
      select.disabled = state.pending;
      quantity.disabled = state.pending || !found || ownership.ambiguous;
      button.disabled = state.pending || !found || ownership.ambiguous || !quantity.validity.valid;
      button.textContent = state.pending ? (state.phase === 'queued' ? 'Waiting…' : 'Updating…') : ownership.quantity > 0 ? (Number(quantity.value) === 0 ? 'Remove from basket' : 'Update quantity') : 'Add to basket';
      button.setAttribute('aria-label', found ? button.textContent + ' — ' + found.product.title : 'Choose an option for ' + card.querySelector('h3').textContent);
      form.setAttribute('aria-busy', String(state.pending));
      const preview = found || options[0];
      if (preview) {
        const choice = card.querySelector('[data-ho-extra-choice]');
        if (choice) { choice.textContent = displayLabel(preview.product, preview.variant); choice.hidden = this.config.context !== 'product' || !defaultId; }
        card.querySelector('[data-ho-extra-link]').setAttribute('aria-label', preview.product.title);
        card.querySelector('[data-ho-extra-link]').href = preview.product.url;
        const image = card.querySelector('[data-ho-extra-image]');
        if (image && preview.product.image) image.src = preview.product.image;
        const frameVisual = card.querySelector('[data-ho-extra-frame-visual]');
        if (frameVisual && window.HarrodBoundaryVisuals) frameVisual.innerHTML = window.HarrodBoundaryVisuals.icon({ kind: 'pleached', custom: true, widthCm: parseFloat(preview.variant.options?.[0]) || 150, heightCm: parseFloat(preview.variant.options?.[1]) || 150 });
      }
      const lowest = options.length ? Math.min(...options.map(item => Number(item.variant.price))) : null;
      card.querySelector('[data-ho-extra-price]').textContent = found ? this.money.format(Number(found.variant.price) / 100) : lowest != null ? 'From ' + this.money.format(lowest / 100) : 'No available options';
      this.nativeAction(form, select, ownership);
      if (ownership.ambiguous && !state.pending) this.showStatus('This option has separate basket lines. Adjust those lines in your basket.', form, true);
    }
    nativeAction(form, select, ownership) {
      let key = form.querySelector('[data-ho-extra-line-id]');
      if (ownership.line && ownership.line.key) {
        if (!key) { key = document.createElement('input'); key.type = 'hidden'; key.dataset.hoExtraLineId = ''; form.append(key); }
        key.name = 'id'; key.value = ownership.line.key;
        select.name = 'variant_id';
        form.action = this.endpoint('cartChange', 'cart/change.js').replace(/\.js(?=\?|$)/, '');
      } else {
        if (key) key.remove();
        select.name = 'id';
        form.action = this.endpoint('cartAdd', 'cart/add.js').replace(/\.js(?=\?|$)/, '');
      }
    }
    endpoint(name, suffix) {
      const root = window.Shopify?.routes?.root || '/';
      const url = new URL(this.config.routes?.[name] || root + suffix, location.origin);
      if (url.origin !== location.origin) throw new Error('Invalid basket endpoint');
      return url.href;
    }
    showStatus(message, form, showBasket) {
      const status = form?.querySelector('[data-ho-extra-status]') || this.root.querySelector('[data-ho-extras-status]');
      if (status) status.textContent = message;
      if (message) this.hasMessage = true;
      if (showBasket) this.root.querySelector('[data-ho-extra-basket]').hidden = false;
    }
    updateHeader(markup) {
      if (typeof markup !== 'string') return;
      const status = new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-ho-cart-status]');
      if (!status || !/^\d+$/.test(status.dataset.count || '')) return;
      const count = Number(status.dataset.count);
      document.querySelectorAll('[data-header-cart-count]').forEach(badge => { badge.setAttribute('data-header-cart-count', String(count)); badge.classList.toggle('visible', count > 0); });
      window.HarrodDeliveryProgress?.update(status);
    }
    async readCart(signal) {
      const response = await fetch(this.endpoint('cartJson', 'cart.js'), { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store', signal });
      if (!response.ok) throw new Error('Basket unavailable');
      const cart = await response.json();
      if (!Array.isArray(cart.items)) throw new Error('Basket unavailable');
      return cart;
    }
    async submit(event) {
      const form = event.target;
      if (!form.matches('[data-ho-extra-form]')) return;
      const card = form.closest('[data-ho-extra-card]');
      const state = this.cardState(card);
      if (state.pending) { event.preventDefault(); event.stopImmediatePropagation(); return; }
      if (!window.fetch) { if (this.config.context === 'cart') saveDraft(captureDraft()); return; }
      event.preventDefault(); event.stopImmediatePropagation();
      if (!form.reportValidity()) return;
      const select = form.querySelector('[data-ho-extra-variant]');
      const selected = this.allowed.find(item => id(item.variant.id) === select.value);
      if (!selected) { this.showStatus('Choose an available option.', form, false); return; }
      const desired = Number(form.querySelector('[data-ho-extra-quantity]').value);
      const expected = state.baseQuantity;
      state.pending = true; state.phase = 'queued'; state.selectedRecord = selected;
      this.updateCard(card);
      await runtime.enqueue(async () => {
        state.phase = 'updating'; this.updateCard(card);
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 20000);
        let attempted = false;
        let confirmed = false;
        let reset = false;
        try {
          const fresh = await this.readCart(controller.signal);
          runtime.publish(fresh);
          const plan = planChange(fresh.items, selected.variant, desired, expected);
          if (plan.kind === 'conflict' || plan.kind === 'error') {
            reset = plan.kind === 'conflict';
            this.showStatus(plan.message, form, true);
            return;
          }
          if (plan.kind === 'none') { reset = true; this.showStatus('Your basket already has this quantity.', form, true); return; }
          const payload = { ...plan.payload, sections: 'ho-cart-status', sections_url: location.pathname + location.search };
          attempted = true;
          const response = await fetch(this.endpoint(plan.kind === 'add' ? 'cartAdd' : 'cartChange', plan.kind === 'add' ? 'cart/add.js' : 'cart/change.js'), { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify(payload), signal: controller.signal });
          const result = await response.json();
          if (!response.ok || result.status) {
            const error = new Error(String(result.description || result.message || 'The basket could not be updated.'));
            error.isCartResponse = true; throw error;
          }
          confirmed = true; reset = true;
          this.updateHeader(result.sections?.['ho-cart-status']);
          let latest;
          try { latest = await this.readCart(controller.signal); }
          catch (_) {
            if (plan.kind === 'change' && Array.isArray(result.items)) latest = result;
            else latest = { ...fresh, items: [...fresh.items, ...list(result.items || (result.id ? result : []))] };
          }
          runtime.publish(latest);
          this.showStatus(desired === 0 ? 'Removed from your basket.' : plan.kind === 'add' ? 'Added to your basket.' : 'Basket quantity updated.', form, true);
        } catch (error) {
          try { runtime.publish(await this.readCart(controller.signal)); reset = true; } catch (_) { /* Never retry an uncertain write. */ }
          const message = error.isCartResponse ? error.message + ' Check your basket before trying again.' : attempted ? 'We could not confirm the update. Check your basket before trying again; nothing will be retried automatically.' : 'We could not check your basket. Nothing was changed.';
          this.showStatus(message, form, true);
        } finally {
          clearTimeout(timer);
          state.pending = false; state.phase = ''; state.selectedRecord = null;
          if (reset) state.dirty = false;
          this.updateCard(card, reset);
          this.syncCustomFrame();
          if (this.config.context === 'cart' && (confirmed || attempted)) runtime.reloadRequested = true;
        }
      });
    }
  }

  window.HarrodPlantingExtrasRuntime = runtime;
  window.addEventListener('cartcount:update', event => {
    if (event.detail && Array.isArray(event.detail.items)) runtime.publish(event.detail);
  });
  function start() {
    restoreDraft();
    runtime.init();
    const containsRoot = node => node.nodeType === 1 && (node.matches('[data-ho-planting-extras]') || node.querySelector('[data-ho-planting-extras]'));
    runtime.rootObserver = new MutationObserver(records => {
      // Hot-reloaded product sections insert new roots; moving cards inside a root does not.
      if (records.some(record => [...record.addedNodes, ...record.removedNodes].some(containsRoot))) runtime.init();
    });
    runtime.rootObserver.observe(document.documentElement, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', () => runtime.init());
})();



